/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import { useStationWebSocket } from "../../../hooks/useStationWebSocket";
import type { Order, WebSocketStatusType } from "../types";

interface StationData {
  stationId: number;
  orders: Order[];
  status: WebSocketStatusType;
  refresh: () => void;
  soundEnabled: boolean;
  soundPending: boolean;
  enableSound: () => void;
}

interface StationWebSocketContextValue {
  stations: StationData[];
}

const StationWebSocketContext =
  createContext<StationWebSocketContextValue | null>(null);

export const useStationWebSocketContext = () => {
  const context = useContext(StationWebSocketContext);
  if (!context) {
    throw new Error(
      "useStationWebSocketContext must be used within StationWebSocketProvider",
    );
  }
  return context;
};

interface StationWebSocketProviderProps {
  stationIds: number[];
  children: ReactNode;
}

// Компонент за една станция - използва hook и подава данните нагоре
interface StationWebSocketWrapperProps {
  stationId: number;
  onUpdate: (stationId: number, data: Omit<StationData, "stationId">) => void;
}

const StationWebSocketWrapper = ({
  stationId,
  onUpdate,
}: StationWebSocketWrapperProps) => {
  const stationData = useStationWebSocket({
    stationId,
    enabled: true,
  });

  useEffect(() => {
    onUpdate(stationId, stationData);
  }, [stationId, stationData, onUpdate]);

  return null;
};

export const StationWebSocketProvider = ({
  stationIds,
  children,
}: StationWebSocketProviderProps) => {
  const [stations, setStations] = useState<StationData[]>([]);

  // Callback за обновяване на данните за една станция
  const handleStationUpdate = useCallback(
    (stationId: number, data: Omit<StationData, "stationId">) => {
      setStations((prev) => {
        const existingIndex = prev.findIndex((s) => s.stationId === stationId);
        const newStationData: StationData = { stationId, ...data };

        if (existingIndex >= 0) {
          // Обновяваме съществуваща станция
          const updated = [...prev];
          updated[existingIndex] = newStationData;
          return updated;
        } else {
          // Добавяме нова станция
          return [...prev, newStationData];
        }
      });
    },
    [],
  );

  // Почистваме станции, които вече не са в списъка
  useEffect(() => {
    setStations((prev) =>
      prev.filter((station) => stationIds.includes(station.stationId)),
    );
  }, [stationIds]);

  return (
    <StationWebSocketContext.Provider value={{ stations }}>
      {stationIds.map((stationId) => (
        <StationWebSocketWrapper
          key={stationId}
          stationId={stationId}
          onUpdate={handleStationUpdate}
        />
      ))}
      {children}
    </StationWebSocketContext.Provider>
  );
};
